import {
  BinaryRating,
  BinaryRatingData,
  BinaryRatingValue,
  BinaryRatingVariant,
} from "@amzn/stencil-react-components/binary-rating";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

interface ContentFeedbackProps {
  dataTestId: string;
}

/**
 * A binary (thumbs up/down) rating component with feedback collection.
 * Shows a “Thank You” message for 5 seconds after submission.
 */
export const ContentFeedback = ({ dataTestId }: ContentFeedbackProps) => {
  const intl = useIntl();
  const [, setHasErrors] = useState(false);
  const [shouldShowMessageBanner, setShouldShowMessageBanner] = useState(false);

  const publishFeedbackMetric = (value: BinaryRatingValue) => {
    trackStructEvent({
      category: Day1EventCategory.ContentFeedback,
      action:
        value == BinaryRatingValue.Yes
          ? Day1EventAction.SelectUsefulContent
          : Day1EventAction.SelectUnusefulContent,
      label: dataTestId.toUpperCase(),
    });
  };

  const handleRatingChange = (_: any, value: BinaryRatingValue) => {
    setHasErrors(false);
    setShouldShowMessageBanner(true);
    publishFeedbackMetric(value);
  };

  const handleSubmit = (data: BinaryRatingData) => {
    if (
      typeof data?.selectedOptions === "undefined" ||
      JSON.stringify(data.selectedOptions).length === 0
    ) {
      setHasErrors(true);
    } else {
      publishFeedbackMetric(data?.selectedRating);
      setHasErrors(false);
      setShouldShowMessageBanner(true);
    }
  };

  return (
    <BinaryRating
      noLabelText={intl.formatMessage({ id: "no-feedback-button-label" })}
      yesLabelText={intl.formatMessage({ id: "yes-feedback-button-label" })}
      messageBannerProps={
        shouldShowMessageBanner
          ? {
              autoDismissAfter: 20_000,
              content: (
                <Text fontWeight="normal">
                  <FormattedMessage id="faq-content-feedback-thank-you-message" />
                </Text>
              ),
            }
          : undefined
      }
      onRatingChange={handleRatingChange}
      variant={BinaryRatingVariant.Inline}
      onSubmit={handleSubmit}
      titleText={intl.formatMessage({ id: "faq-content-feedback" })}
    />
  );
};
